import { Link, NavLink, Outlet } from "react-router-dom";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BRAND } from "@/brand.config";

const navItems = [
  { to: "/about-program", label: "About Program" },
  { to: "/loyalty-benefits", label: "Benefits" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/rewards", label: "Rewards" },
  { to: "/referral-program", label: "Refer & Earn" },
  { to: "/store-locator", label: "Find a Store" },
  { to: "/faqs", label: "FAQs" },
];

export default function PublicLayout() {
  const [cms, setCms] = useState(null);
  useEffect(() => { api.get("/cms/content").then((r) => setCms(r.data)).catch(() => {}); }, []);
  const topbar = cms?.home?.topbar_text || "EXCLUSIVE LOYALTY PROGRAM · EARN ON EVERY PURCHASE · BIRTHDAY PRIVILEGES INSIDE";
  const tagline = cms?.footer?.tagline || BRAND.footerTagline;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--kazo-cream)" }}>
      {/* Top utility bar */}
      <div className="kazo-bg-black text-xs tracking-widest text-white py-2 text-center font-medium" style={{ letterSpacing: "0.15em" }}>
        {topbar}
      </div>

      {/* Main header */}
      <header className="border-b border-black/10 bg-[#F9F8F6] sticky top-0 z-40 backdrop-blur" data-testid="public-header">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo-link">
            <img src={BRAND.logoUrl} alt={BRAND.name} className="h-10 w-auto object-contain" data-testid="public-brand-logo" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 hidden sm:inline">Rewards</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-[13px] tracking-wide">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} className={({isActive}) => `editorial-link ${isActive ? 'kazo-text-burgundy font-semibold' : ''}`} data-testid={`nav-${n.to.slice(1)}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/enterprise/login" className="k-btn k-btn-outline k-btn-sm hidden md:inline-flex" data-testid="header-enterprise-login">Enterprise Login</Link>
            <Link to="/" className="k-btn k-btn-sm kazo-bg-burgundy text-white" data-testid="header-join-now">Join Free</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="kazo-bg-black text-white mt-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div className="font-display text-3xl tracking-tight" style={{ fontWeight: 500 }}>{BRAND.name}</div>
            <p className="mt-3 text-sm text-white/60 max-w-xs leading-relaxed">
              {tagline}
            </p>
            <div className="mt-6 flex gap-4">
              <a href={BRAND.social.instagram} className="text-white/70 hover:text-white" aria-label="Odhni on Instagram" data-testid="footer-instagram-link"><Instagram className="w-5 h-5" /></a>
              <a href={BRAND.social.facebook} className="text-white/70 hover:text-white" aria-label="Odhni on Facebook" data-testid="footer-facebook-link"><Facebook className="w-5 h-5" /></a>
              <a href={BRAND.social.youtube} className="text-white/70 hover:text-white" aria-label="Odhni on YouTube" data-testid="footer-youtube-link"><Youtube className="w-5 h-5" /></a>
            </div>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Programme</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/about-program" className="hover:text-white" data-testid="footer-about-link">About Program</Link></li>
              <li><Link to="/loyalty-benefits" className="hover:text-white" data-testid="footer-benefits-link">Benefits</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white" data-testid="footer-how-it-works-link">How It Works</Link></li>
              <li><Link to="/rewards" className="hover:text-white" data-testid="footer-rewards-link">Rewards</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Engage</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/earn-points" className="hover:text-white" data-testid="footer-earn-link">Earn Points</Link></li>
              <li><Link to="/redeem-points" className="hover:text-white" data-testid="footer-redeem-link">Redeem Points</Link></li>
              <li><Link to="/referral-program" className="hover:text-white" data-testid="footer-referral-link">Refer & Earn</Link></li>
              <li><Link to="/store-locator" className="hover:text-white" data-testid="footer-store-locator-link">Find a Store</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Portals</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/enterprise/login" className="hover:text-white" data-testid="footer-enterprise-login">Enterprise Login</Link></li>
              <li><Link to="/store/login" className="hover:text-white" data-testid="footer-store-login">Store Login</Link></li>
              <li><Link to="/crm/login" className="hover:text-white" data-testid="footer-crm-login">CRM Login</Link></li>
              <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact-link">Customer Support</Link></li>
              <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 px-6 lg:px-12 max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between text-xs text-white/40 gap-2">
          <div>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</div>
          <div className="flex items-center gap-2">
            <span className="tracking-[0.15em] uppercase">Powered by</span>
            <img src={BRAND.platformLogoUrl} alt={BRAND.platform} className="h-3.5 w-auto opacity-80" />
          </div>
        </div>
      </footer>
    </div>
  );
}
